import * as React from 'react';
import ky, {HTTPError} from 'ky';
import { useAnalytics } from './AnalyticsProvider';
import { useConfig } from './ConfigProvider';
import { useContractContext } from './ContractProvider';
import { useModalSettingsContext } from './ModalSettingsProvider';
import { usePostMessageContext, usePostMessageHandler } from './PostMessageProvider';

interface AffirmTerm {
  amount: number;
  apr: number;
  billing_frequency: string;
  downpayment_amount: null | number;
  installment_amount: number;
  installment_count: number;
  interest_amount: number;
  loan_type: string;
  minimum_loan_amount: number;
}

const AffirmContext = React.createContext({
  learnMoreUrl: '',
  message: '',
});

// TODO: move to util?
const getCurrentTime = () => new Date().getTime();

export const AffirmProvider = ({ children }: { children: React.ReactNode }) => {
  const { track } = useAnalytics();
  const { affirm, cartType } = useConfig();
  const { productPrice, selectedContract } = useContractContext();
  const { modalType, openedBy } = useModalSettingsContext();
  const { postMessage } = usePostMessageContext();
  const [useParentWindowForApiRequest] = React.useState(false); // TODO
  const [currentOffer, setCurrentOffer] = React.useState<AffirmTerm | null>(null);
  const cache = React.useRef(new Map<string, unknown>());
  const [affirmAppended, setAffirmAppended] = React.useState(false);

  // Affirm requires a selected contract, a product price, and a valid configuration
  const affirmActive = React.useMemo(() => {
    if (!affirm?.pubKey || !affirm?.apiUrl || !affirm.showAffirm) {
      return false;
    }
    const maximumAmount = affirm?.maximumAmount || 0;
    const minimumAmount = affirm?.minimumAmount || 0;
    if (productPrice < minimumAmount || productPrice > maximumAmount) {
      return false;
    }
    if (!selectedContract) {
      return false;
    }
    return true;
  }, [affirm, selectedContract, productPrice]);

  const priceString = React.useMemo(() => {
    return (productPrice + Number(selectedContract?.price)) * 100;
  }, [productPrice, selectedContract?.price]);

  // Generate the URL for the API call
  const affirmUrl = React.useMemo(() => {
    if (affirmActive && affirm) {
      return `${affirm.apiUrl.replace(/\/+$/, '')}/${affirm.pubKey}?amount=${priceString}&use_best_terms=true`;
    }
    return '';
  }, [affirm, affirmActive, priceString]);

  const trackRequestAnalytics = React.useCallback((summary: string, message: string, timing: number) => {
    track('affirm_api_analytics', {
      ctaVersion: '1.0.0',
      paymentOption: 'affirm',
      dataUrl: affirmUrl,
      type: 'shopify.affirmRequest', // TODO: other platforms?
      summary: summary || '',
      message: message || '',
      timing: timing || 0
    });
  }, [track, affirmUrl]);

  const fetchAffirmDetails = React.useCallback(async () => {
    if (useParentWindowForApiRequest) {
      postMessage('affirmRequest', {
        url: affirmUrl,
        price: productPrice
      });
      return;
    }

    if (cache.current.has(affirmUrl)) {
      return cache.current.get(affirmUrl);
    }

    const start = getCurrentTime();
    try {
      trackRequestAnalytics('pre request', '', 0);
      const response = await ky.get(affirmUrl).json();
      trackRequestAnalytics('request success', '', getCurrentTime() - start);
      handleResponse(response);
      cache.current.set(affirmUrl, response);
    } catch (e) {
      if (e instanceof HTTPError) {
        const json = await e.response.json();
        trackRequestAnalytics('request error', e.response.status + ' ' + json.message, getCurrentTime() - start);
      }
    }
  }, [postMessage, useParentWindowForApiRequest, affirmUrl, productPrice, trackRequestAnalytics]);

  // Do we need to fire off an updated affirm request?
  React.useEffect(() => {
    if (affirmActive) {
      fetchAffirmDetails();
    }
  }, [affirmActive, selectedContract]);

  const handleResponse = React.useCallback((detail: any) => { // eslint-disable-line
    if (!detail?.offer?.terms || !detail.offer.terms.length) {
      console.warn('Invalid affirm response', { detail });
      return;
    }

    const term = detail.offer.terms.sort(
      (termA: AffirmTerm, termB: AffirmTerm) => termA.installment_amount - termB.installment_amount
    )[0] as AffirmTerm;

    setCurrentOffer(term);

    if (!affirmAppended) {
      setAffirmAppended(true);
      if (openedBy === 'cart') {
        postMessage('affirmAppended', {
          type: 'modal',
          modalType,
          cartType,
        });
      }
    }
  }, [setCurrentOffer, affirmAppended, setAffirmAppended, postMessage, openedBy, cartType]);

  usePostMessageHandler('affirmResponse', React.useCallback((e: Event) => {
    const event = e as CustomEvent<any>; // eslint-disable-line
    handleResponse(event.detail.response);
  }, [handleResponse]));

  const learnMoreUrl = React.useMemo(() => {
    return productPrice > 0
      ? `https://www.affirm.com/apps/prequal/?public_api_key=${affirm?.pubKey}&unit_price=${priceString}&page_type=product&use_promo=true`
      : 'https://www.affirm.com/how-it-works';
  }, [productPrice, priceString, affirm?.pubKey]);

  const message = React.useMemo(() => {
    if (!currentOffer) {
      return '';
    }
    const amountDollars = Math.ceil(currentOffer.installment_amount / 100);
    const frequency = currentOffer.billing_frequency;
    return `$${amountDollars} ${frequency === 'monthly' ? '/month' : '/2 weeks'}`;
  }, [currentOffer]);

  if (!currentOffer) {
    return null;
  }

  return (
    <AffirmContext.Provider value={{ learnMoreUrl, message }}>
      {children}
    </AffirmContext.Provider>
  );
};

export const useAffirm = () => React.useContext(AffirmContext);
